import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi, UserInfo } from '../services/authApi';
import { artistApi } from '../services/artistApi';
import { trackApi } from '../services/trackApi';
import { feedbackApi } from '../services/feedbackApi';
import { adminApi } from '../services/adminApi';
import { Artist, Track, TrackReport, FeedbackReport, ReportStatsResponse, Feedback as FeedbackType } from '../types/artist';
import { TransportControl } from './shared/TransportControl';
import { Feedback } from './shared/Feedback';
import './AdminDashboard.css';

interface AdminDashboardProps {
  onExitAdmin: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onExitAdmin }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [artistTracks, setArtistTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'artists' | 'reports'>('users');
  
  // Pagination state for users
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize] = useState(12);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  
  // Pagination state for artists
  const [artistsPage, setArtistsPage] = useState(1);
  const [artistsPageSize] = useState(12);
  const [artistsTotalPages, setArtistsTotalPages] = useState(1);
  const [artistsTotalCount, setArtistsTotalCount] = useState(0);

  // Reports state
  const [reportStats, setReportStats] = useState<ReportStatsResponse | null>(null);
  const [trackReports, setTrackReports] = useState<TrackReport[]>([]);
  const [feedbackReports, setFeedbackReports] = useState<FeedbackReport[]>([]);
  const [reportsSubTab, setReportsSubTab] = useState<'tracks' | 'feedback'>('tracks');
  const [reportsFilter, setReportsFilter] = useState<'all' | 'unresolved' | 'resolved'>('unresolved');
  const [loadingReports, setLoadingReports] = useState(false);
  const [trackReportsPage, setTrackReportsPage] = useState(1);
  const [trackReportsTotalPages, setTrackReportsTotalPages] = useState(1);
  const [trackReportsTotalCount, setTrackReportsTotalCount] = useState(0);
  const [feedbackReportsPage, setFeedbackReportsPage] = useState(1);
  const [feedbackReportsTotalPages, setFeedbackReportsTotalPages] = useState(1);
  const [feedbackReportsTotalCount, setFeedbackReportsTotalCount] = useState(0);
  const [resolvingReportId, setResolvingReportId] = useState<number | null>(null);
  const [resolutionText, setResolutionText] = useState('');

  // Track playback state for reported tracks
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingTrack, setPlayingTrack] = useState<Track | null>(null);
  const [playingReportId, setPlayingReportId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [loadingTrackId, setLoadingTrackId] = useState<number | null>(null);

  // Feedback display state for reported feedback
  const [expandedFeedbackId, setExpandedFeedbackId] = useState<number | null>(null);
  const [feedbackDetails, setFeedbackDetails] = useState<FeedbackType | null>(null);
  const [loadingFeedbackId, setLoadingFeedbackId] = useState<number | null>(null);

  // Selected track state for Artists tab
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load users and artists in parallel with pagination
      const [usersData, artistsData] = await Promise.all([
        authApi.getAllUsers(usersPage, usersPageSize),
        artistApi.getAllArtists(artistsPage, artistsPageSize)
      ]);

      setUsers(usersData.items);
      setUsersTotalPages(usersData.totalPages);
      setUsersTotalCount(usersData.totalCount);
      
      setArtists(artistsData.items);
      setArtistsTotalPages(artistsData.totalPages);
      setArtistsTotalCount(artistsData.totalCount);
    } catch (err) {
      console.error('Failed to load admin data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [usersPage, usersPageSize, artistsPage, artistsPageSize]);

  useEffect(() => {
    // Check if user is admin
    if (!user?.roles?.includes('Admin')) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }

    loadData();
    // Load report stats for the badge
    loadReportStats();
  }, [user, loadData]);
  
  const loadUsers = async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const usersData = await authApi.getAllUsers(page, usersPageSize);
      setUsers(usersData.items);
      setUsersTotalPages(usersData.totalPages);
      setUsersTotalCount(usersData.totalCount);
      setUsersPage(page);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const loadArtists = async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const artistsData = await artistApi.getAllArtists(page, artistsPageSize);
      setArtists(artistsData.items);
      setArtistsTotalPages(artistsData.totalPages);
      setArtistsTotalCount(artistsData.totalCount);
      setArtistsPage(page);
      
      // Clear selected artist and track when changing pages
      setSelectedArtist(null);
      setArtistTracks([]);
      setSelectedTrack(null);
    } catch (err) {
      console.error('Failed to load artists:', err);
      setError(err instanceof Error ? err.message : 'Failed to load artists');
    } finally {
      setLoading(false);
    }
  };

  const loadReportStats = async () => {
    try {
      const stats = await adminApi.getReportStats();
      setReportStats(stats);
    } catch (err) {
      console.error('Failed to load report stats:', err);
    }
  };

  const getResolvedFilter = useCallback((): boolean | undefined => {
    if (reportsFilter === 'unresolved') return false;
    if (reportsFilter === 'resolved') return true;
    return undefined;
  }, [reportsFilter]);

  const loadTrackReports = useCallback(async (page: number = 1) => {
    try {
      setLoadingReports(true);
      const data = await adminApi.getTrackReports(page, 10, getResolvedFilter());
      setTrackReports(data.items);
      setTrackReportsPage(data.page);
      setTrackReportsTotalPages(data.totalPages);
      setTrackReportsTotalCount(data.totalCount);
    } catch (err) {
      console.error('Failed to load track reports:', err);
      setError(err instanceof Error ? err.message : 'Failed to load track reports');
    } finally {
      setLoadingReports(false);
    }
  }, [getResolvedFilter]);

  const loadFeedbackReports = useCallback(async (page: number = 1) => {
    try {
      setLoadingReports(true);
      const data = await adminApi.getFeedbackReports(page, 10, getResolvedFilter());
      setFeedbackReports(data.items);
      setFeedbackReportsPage(data.page);
      setFeedbackReportsTotalPages(data.totalPages);
      setFeedbackReportsTotalCount(data.totalCount);
    } catch (err) {
      console.error('Failed to load feedback reports:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feedback reports');
    } finally {
      setLoadingReports(false);
    }
  }, [getResolvedFilter]);

  // Load reports when tab or filter changes
  useEffect(() => {
    if (activeTab === 'reports') {
      loadReportStats();
      if (reportsSubTab === 'tracks') {
        loadTrackReports(1);
      } else {
        loadFeedbackReports(1);
      }
    }
  }, [activeTab, reportsSubTab, loadTrackReports, loadFeedbackReports]);

  const handleResolveReport = async (reportId: number, type: 'track' | 'feedback') => {
    if (!resolutionText.trim()) {
      setError('Please enter a resolution');
      return;
    }

    try {
      if (type === 'track') {
        await adminApi.resolveTrackReport(reportId, { resolution: resolutionText });
        loadTrackReports(trackReportsPage);
      } else {
        await adminApi.resolveFeedbackReport(reportId, { resolution: resolutionText });
        loadFeedbackReports(feedbackReportsPage);
      }
      loadReportStats();
      setResolvingReportId(null);
      setResolutionText('');
    } catch (err) {
      console.error('Failed to resolve report:', err);
      setError(err instanceof Error ? err.message : 'Failed to resolve report');
    }
  };

  const handleDeleteReport = async (reportId: number, type: 'track' | 'feedback') => {
    if (!window.confirm('Are you sure you want to dismiss this report? The report will be deleted but the content will remain.')) {
      return;
    }

    try {
      if (type === 'track') {
        await adminApi.deleteTrackReport(reportId);
        loadTrackReports(trackReportsPage);
      } else {
        await adminApi.deleteFeedbackReport(reportId);
        loadFeedbackReports(feedbackReportsPage);
      }
      loadReportStats();
    } catch (err) {
      console.error('Failed to delete report:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete report');
    }
  };

  // Delete content and resolve report
  const handleDeleteTrackAndResolve = async (report: TrackReport) => {
    if (!window.confirm(`Are you sure you want to DELETE the track "${report.trackTitle}" and resolve this report? This action cannot be undone.`)) {
      return;
    }

    try {
      // Stop playing if this track is playing
      if (playingReportId === report.id) {
        handleClosePlayer();
      }

      // Delete the track first
      await trackApi.deleteTrack(report.trackId);
      
      // Then resolve the report with a note about deletion
      await adminApi.resolveTrackReport(report.id, { 
        resolution: 'Track deleted due to content violation.' 
      });
      
      loadTrackReports(trackReportsPage);
      loadReportStats();
    } catch (err) {
      console.error('Failed to delete track and resolve report:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete track');
    }
  };

  const handleDeleteFeedbackAndResolve = async (report: FeedbackReport) => {
    if (!window.confirm(`Are you sure you want to DELETE this feedback and resolve the report? This action cannot be undone.`)) {
      return;
    }

    try {
      // Close feedback display if expanded
      if (expandedFeedbackId === report.id) {
        setExpandedFeedbackId(null);
        setFeedbackDetails(null);
      }

      // Delete the feedback first
      await feedbackApi.deleteFeedback(report.feedbackId);
      
      // Then resolve the report with a note about deletion
      await adminApi.resolveFeedbackReport(report.id, { 
        resolution: 'Feedback deleted due to content violation.' 
      });
      
      loadFeedbackReports(feedbackReportsPage);
      loadReportStats();
    } catch (err) {
      console.error('Failed to delete feedback and resolve report:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete feedback');
    }
  };

  // Audio playback handlers for reported tracks
  const handlePlayReportedTrack = async (report: TrackReport) => {
    try {
      // If already playing this track, toggle play/pause
      if (playingReportId === report.id && playingTrack) {
        handlePlayPause();
        return;
      }

      setLoadingTrackId(report.id);
      const track = await trackApi.getTrackById(report.trackId);
      
      // Stop any currently playing track
      if (audioRef.current) {
        audioRef.current.pause();
      }

      setPlayingTrack(track);
      setPlayingReportId(report.id);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(true);
      setLoadingTrackId(null);
    } catch (err) {
      console.error('Failed to load track:', err);
      setError(err instanceof Error ? err.message : 'Failed to load track');
      setLoadingTrackId(null);
    }
  };

  const handleClosePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlayingTrack(null);
    setPlayingReportId(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
    setCurrentTime(time);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
    setVolume(vol);
    if (vol > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const handleMuteToggle = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Feedback display handlers for reported feedback
  const handleViewFeedback = async (report: FeedbackReport) => {
    try {
      // Toggle off if already expanded
      if (expandedFeedbackId === report.id) {
        setExpandedFeedbackId(null);
        setFeedbackDetails(null);
        return;
      }

      setLoadingFeedbackId(report.id);
      const feedback = await feedbackApi.getFeedbackById(report.feedbackId);
      setFeedbackDetails(feedback);
      setExpandedFeedbackId(report.id);
      setLoadingFeedbackId(null);
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
      setLoadingFeedbackId(null);
    }
  };

  const handleArtistClick = async (artist: Artist) => {
    if (selectedArtist?.id === artist.id) {
      // Close if same artist clicked
      setSelectedArtist(null);
      setArtistTracks([]);
      setSelectedTrack(null);
      return;
    }

    setSelectedArtist(artist);
    setLoadingTracks(true);
    setArtistTracks([]);
    setSelectedTrack(null);

    try {
      // Get tracks for this user (more efficient than getAllTracks)
      const tracks = await trackApi.getTracksByUserId(artist.authorizedUserId);
      // Filter tracks for this specific artist (user might have multiple artist profiles)
      const artistTracks = tracks.filter(track => track.artistId === artist.id);
      setArtistTracks(artistTracks);
    } catch (err) {
      console.error('Failed to load artist tracks:', err);
      setError('Failed to load tracks for this artist');
    } finally {
      setLoadingTracks(false);
    }
  };

  const formatUserRoles = (roles: string[]) => {
    return roles.length > 0 ? roles.join(', ') : 'User';
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getUserEmailById = (userId: string) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.email : 'Unknown user';
  };
  
  const renderPagination = (
    currentPage: number,
    totalPages: number,
    totalCount: number,
    onPageChange: (page: number) => void
  ) => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return (
      <div className="pagination">
        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        
        {startPage > 1 && (
          <>
            <button
              className="pagination-button"
              onClick={() => onPageChange(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {pages.map(page => (
          <button
            key={page}
            className={`pagination-button ${page === currentPage ? 'active' : ''}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button
              className="pagination-button"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        
        <span className="pagination-info">
          Page {currentPage} of {totalPages} ({totalCount} total)
        </span>
      </div>
    );
  };

  if (!user?.roles?.includes('Admin')) {
    return (
      <div className="admin-dashboard">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading admin dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <p>Welcome, {user.firstName} {user.lastName}</p>
        </div>
        <button 
          className="return-to-main-button"
          onClick={onExitAdmin}
          title="Return to main site"
        >
          Return to Main Site
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users ({usersTotalCount})
        </button>
        <button
          className={`tab-button ${activeTab === 'artists' ? 'active' : ''}`}
          onClick={() => setActiveTab('artists')}
        >
          Artists ({artistsTotalCount})
        </button>
        <button
          className={`tab-button ${activeTab === 'reports' ? 'active' : ''} ${reportStats && reportStats.trackReports.unresolved + reportStats.feedbackReports.unresolved > 0 ? 'has-unresolved' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports {reportStats && `(${reportStats.trackReports.unresolved + reportStats.feedbackReports.unresolved} pending)`}
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="users-section">
            <h2>Registered Users</h2>
            <div className="users-table">
              <div className="table-header">
                <div className="header-cell">Name</div>
                <div className="header-cell">Email</div>
                <div className="header-cell">Roles</div>
                <div className="header-cell">ID</div>
              </div>
              {users.map(user => (
                <div key={user.id} className="table-row">
                  <div className="table-cell">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="table-cell">{user.email}</div>
                  <div className="table-cell">
                    <span className={`role-badge ${user.roles.includes('Admin') ? 'admin' : 'user'}`}>
                      {formatUserRoles(user.roles)}
                    </span>
                  </div>
                  <div className="table-cell user-id">{user.id}</div>
                </div>
              ))}
            </div>
            {renderPagination(usersPage, usersTotalPages, usersTotalCount, loadUsers)}
          </div>
        )}

        {activeTab === 'artists' && (
          <div className="artists-section">
            <h2>Artists</h2>
            <div className="artists-grid">
              {artists.map(artist => (
                <div
                  key={artist.id}
                  className={`artist-card ${selectedArtist?.id === artist.id ? 'selected' : ''}`}
                  onClick={() => handleArtistClick(artist)}
                >
                  <div className="artist-header">
                    <h3>{artist.name}</h3>
                    <span className={`status-badge ${artist.isActive ? 'active' : 'inactive'}`}>
                      {artist.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="artist-details">
                    <p><strong>Karma:</strong> {artist.karma}</p>
                    <p><strong>User Email:</strong> {getUserEmailById(artist.authorizedUserId)}</p>
                    <p><strong>Artist ID:</strong> {artist.id}</p>
                  </div>
                </div>
              ))}
            </div>
            {renderPagination(artistsPage, artistsTotalPages, artistsTotalCount, loadArtists)}

            {selectedArtist && (
              <div className="artist-tracks-section">
                <h3>Tracks by {selectedArtist.name}</h3>
                {loadingTracks ? (
                  <div className="loading">Loading tracks...</div>
                ) : artistTracks.length === 0 ? (
                  <p className="no-tracks">No tracks found for this artist.</p>
                ) : (
                  <div className="tracks-table">
                    <div className="table-header">
                      <div className="header-cell">Title</div>
                      <div className="header-cell">Genre</div>
                      <div className="header-cell">Upload Date</div>
                      <div className="header-cell">Status</div>
                      <div className="header-cell">Feedback</div>
                    </div>
                    {artistTracks.map(track => (
                      <div 
                        key={track.id} 
                        className={`table-row clickable ${selectedTrack?.id === track.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTrack(selectedTrack?.id === track.id ? null : track)}
                      >
                        <div className="table-cell track-title">{track.title}</div>
                        <div className="table-cell">{track.genre || 'N/A'}</div>
                        <div className="table-cell">{formatDate(track.uploadDate)}</div>
                        <div className="table-cell">
                          <span className={`status-badge ${track.underReview ? 'under-review' : 'pending'}`}>
                            {track.underReview ? 'Under Review' : 'Pending'}
                          </span>
                        </div>
                        <div className="table-cell">
                          {track.feedback && track.feedback.length > 0 
                            ? `${track.feedback.length} review${track.feedback.length !== 1 ? 's' : ''}`
                            : 'No feedback'
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Track Feedback Display */}
                {selectedTrack && (
                  <div className="track-feedback-section">
                    <h4>Feedback for "{selectedTrack.title}"</h4>
                    {!selectedTrack.feedback || selectedTrack.feedback.length === 0 ? (
                      <p className="no-feedback">No feedback has been submitted for this track yet.</p>
                    ) : (
                      <div className="feedback-list">
                        {selectedTrack.feedback.map((fb) => (
                          <Feedback
                            key={fb.id}
                            feedback={fb}
                            showAuthor={true}
                            variant="card"
                            showThreadButton={false}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="reports-section">
            <h2>Content Reports</h2>
            
            {/* Report Stats Summary */}
            {reportStats && (
              <div className="report-stats">
                <div className="stat-card">
                  <h4>Track Reports</h4>
                  <div className="stat-numbers">
                    <span className="stat-unresolved">{reportStats.trackReports.unresolved} pending</span>
                    <span className="stat-resolved">{reportStats.trackReports.resolved} resolved</span>
                    <span className="stat-total">{reportStats.trackReports.total} total</span>
                  </div>
                </div>
                <div className="stat-card">
                  <h4>Feedback Reports</h4>
                  <div className="stat-numbers">
                    <span className="stat-unresolved">{reportStats.feedbackReports.unresolved} pending</span>
                    <span className="stat-resolved">{reportStats.feedbackReports.resolved} resolved</span>
                    <span className="stat-total">{reportStats.feedbackReports.total} total</span>
                  </div>
                </div>
              </div>
            )}

            {/* Sub-tabs for Track vs Feedback reports */}
            <div className="reports-sub-tabs">
              <button
                className={`sub-tab-button ${reportsSubTab === 'tracks' ? 'active' : ''}`}
                onClick={() => setReportsSubTab('tracks')}
              >
                Track Reports ({trackReportsTotalCount})
              </button>
              <button
                className={`sub-tab-button ${reportsSubTab === 'feedback' ? 'active' : ''}`}
                onClick={() => setReportsSubTab('feedback')}
              >
                Feedback Reports ({feedbackReportsTotalCount})
              </button>
            </div>

            {/* Filter controls */}
            <div className="reports-filter">
              <label>Filter:</label>
              <select 
                value={reportsFilter} 
                onChange={(e) => setReportsFilter(e.target.value as 'all' | 'unresolved' | 'resolved')}
              >
                <option value="unresolved">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="all">All</option>
              </select>
            </div>

            {loadingReports ? (
              <div className="loading">Loading reports...</div>
            ) : (
              <>
                {/* Track Reports List */}
                {reportsSubTab === 'tracks' && (
                  <div className="reports-list">
                    {trackReports.length === 0 ? (
                      <p className="no-reports">No track reports found.</p>
                    ) : (
                      trackReports.map(report => (
                        <div key={report.id} className={`report-card ${report.resolved ? 'resolved' : 'pending'}`}>
                          <div className="report-header">
                            <div className="report-info">
                              <h4>🎵 {report.trackTitle}</h4>
                              <span className="report-artist">by {report.artistName}</span>
                            </div>
                            <div className="report-header-actions">
                              <button
                                className="action-btn play"
                                onClick={() => handlePlayReportedTrack(report)}
                                disabled={loadingTrackId === report.id}
                              >
                                {loadingTrackId === report.id ? '⏳' : playingReportId === report.id && isPlaying ? '⏸️ Pause' : '▶️ Play'}
                              </button>
                              <span className={`status-badge ${report.resolved ? 'resolved' : 'pending'}`}>
                                {report.resolved ? 'Resolved' : 'Pending'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Transport Control for playing track */}
                          {playingReportId === report.id && playingTrack && (
                            <div className="report-player">
                              <TransportControl
                                track={playingTrack}
                                isPlaying={isPlaying}
                                currentTime={currentTime}
                                duration={duration}
                                volume={volume}
                                isMuted={isMuted}
                                onPlayPause={handlePlayPause}
                                onSeek={handleSeek}
                                onVolumeChange={handleVolumeChange}
                                onMuteToggle={handleMuteToggle}
                                showCloseButton={true}
                                onClose={handleClosePlayer}
                                audioRef={audioRef}
                                variant="minimal"
                                className="admin-transport"
                              />
                              <audio
                                ref={audioRef}
                                src={playingTrack.id ? trackApi.getAudioStreamUrl(playingTrack.id) : playingTrack.audioUrl}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onEnded={handleAudioEnded}
                                autoPlay
                              />
                            </div>
                          )}

                          <div className="report-details">
                            <p><strong>Reason:</strong> {report.reason}</p>
                            <p><strong>Reported:</strong> {formatDate(report.createdAt)}</p>
                            <p><strong>Reported by:</strong> {report.reportedByUserId}</p>
                            {report.resolved && (
                              <>
                                <p><strong>Resolution:</strong> {report.resolution}</p>
                                <p><strong>Resolved:</strong> {report.resolvedAt ? formatDate(report.resolvedAt) : 'N/A'}</p>
                              </>
                            )}
                          </div>
                          {!report.resolved && (
                            <div className="report-actions">
                              {resolvingReportId === report.id ? (
                                <div className="resolve-form">
                                  <textarea
                                    placeholder="Enter resolution notes..."
                                    value={resolutionText}
                                    onChange={(e) => setResolutionText(e.target.value)}
                                    rows={3}
                                  />
                                  <div className="resolve-buttons">
                                    <button 
                                      className="resolve-btn"
                                      onClick={() => handleResolveReport(report.id, 'track')}
                                    >
                                      Resolve
                                    </button>
                                    <button 
                                      className="cancel-btn"
                                      onClick={() => { setResolvingReportId(null); setResolutionText(''); }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <button 
                                    className="action-btn resolve"
                                    onClick={() => setResolvingReportId(report.id)}
                                  >
                                    Resolve
                                  </button>
                                  <button 
                                    className="action-btn danger"
                                    onClick={() => handleDeleteTrackAndResolve(report)}
                                  >
                                    🗑️ Delete Track & Resolve
                                  </button>
                                  <button 
                                    className="action-btn dismiss"
                                    onClick={() => handleDeleteReport(report.id, 'track')}
                                  >
                                    Dismiss
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    {renderPagination(trackReportsPage, trackReportsTotalPages, trackReportsTotalCount, loadTrackReports)}
                  </div>
                )}

                {/* Feedback Reports List */}
                {reportsSubTab === 'feedback' && (
                  <div className="reports-list">
                    {feedbackReports.length === 0 ? (
                      <p className="no-reports">No feedback reports found.</p>
                    ) : (
                      feedbackReports.map(report => (
                        <div key={report.id} className={`report-card ${report.resolved ? 'resolved' : 'pending'}`}>
                          <div className="report-header">
                            <div className="report-info">
                              <h4>💬 Feedback on "{report.trackTitle}"</h4>
                              <span className="report-artist">by {report.artistName}</span>
                            </div>
                            <div className="report-header-actions">
                              <button
                                className="action-btn view"
                                onClick={() => handleViewFeedback(report)}
                                disabled={loadingFeedbackId === report.id}
                              >
                                {loadingFeedbackId === report.id ? '⏳' : expandedFeedbackId === report.id ? '🔼 Hide' : '👁️ View'}
                              </button>
                              <span className={`status-badge ${report.resolved ? 'resolved' : 'pending'}`}>
                                {report.resolved ? 'Resolved' : 'Pending'}
                              </span>
                            </div>
                          </div>

                          {/* Expanded Feedback Display */}
                          {expandedFeedbackId === report.id && feedbackDetails && (
                            <div className="report-feedback-display">
                              <Feedback
                                feedback={feedbackDetails}
                                showAuthor={false}
                                variant="card"
                                showThreadButton={false}
                              />
                            </div>
                          )}

                          <div className="report-details">
                            <p><strong>Reason:</strong> {report.reason}</p>
                            <p><strong>Reported:</strong> {formatDate(report.createdAt)}</p>
                            <p><strong>Reported by:</strong> {report.reportedByUserId}</p>
                            <p><strong>Feedback by:</strong> {report.feedbackCreatedByUserId}</p>
                            {report.resolved && (
                              <>
                                <p><strong>Resolution:</strong> {report.resolution}</p>
                                <p><strong>Resolved:</strong> {report.resolvedAt ? formatDate(report.resolvedAt) : 'N/A'}</p>
                              </>
                            )}
                          </div>
                          {!report.resolved && (
                            <div className="report-actions">
                              {resolvingReportId === report.id ? (
                                <div className="resolve-form">
                                  <textarea
                                    placeholder="Enter resolution notes..."
                                    value={resolutionText}
                                    onChange={(e) => setResolutionText(e.target.value)}
                                    rows={3}
                                  />
                                  <div className="resolve-buttons">
                                    <button 
                                      className="resolve-btn"
                                      onClick={() => handleResolveReport(report.id, 'feedback')}
                                    >
                                      Resolve
                                    </button>
                                    <button 
                                      className="cancel-btn"
                                      onClick={() => { setResolvingReportId(null); setResolutionText(''); }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <button 
                                    className="action-btn resolve"
                                    onClick={() => setResolvingReportId(report.id)}
                                  >
                                    Resolve
                                  </button>
                                  <button 
                                    className="action-btn danger"
                                    onClick={() => handleDeleteFeedbackAndResolve(report)}
                                  >
                                    🗑️ Delete Feedback & Resolve
                                  </button>
                                  <button 
                                    className="action-btn dismiss"
                                    onClick={() => handleDeleteReport(report.id, 'feedback')}
                                  >
                                    Dismiss
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    {renderPagination(feedbackReportsPage, feedbackReportsTotalPages, feedbackReportsTotalCount, loadFeedbackReports)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;